import { z } from 'zod';

// 02.3-client.md paso a′ — schemas para las formas reales de request/response de este dominio,
// verificadas contra los DTOs y casos de uso del backend (`backend/src/client/domain/*DTO.js`,
// `backend/src/client/application/*`), no inventadas desde cómo el frontend las consume hoy.
// `.parse()` en todos lados (nunca `.safeParse()`) -- mismo criterio que `features/map/schemas.js`
// y `features/tasks/schemas.js`, los únicos 2 precedentes de schemas en el repo: ninguno usa
// `.safeParse()`, así que no hay una razón real todavía para degradarse con gracia en vez de
// tronar visiblemente. `z.looseObject` en los DTOs -- el backend puede agregar campos sin que
// esto rompa (mismo criterio que `map/schemas.js`).
//
// WhatsApp (fetchWhatsAppStatus/Chats/Messages/connect/send/logout) NO tiene schema todavía --
// depende de una decisión de arquitectura pendiente (setQueryData vs. invalidación, 02.3-client.md
// §3/§7 riesgo 6b) que no le corresponde a este paso.

const nullish = (schema) => schema.nullish();
const id = z.union([z.string(), z.number()]);

// ── Op (ClientOperationDTO) ─────────────────────────────────────────────────────────────────
// Campos reales de `toClientOperationDTO` (backend/src/client/domain/ClientOperationDTO.js) --
// `client_nick`/`client_img`/`client_email`/`oper_nick`/`oper_img` solo llegan en `listOps`
// (join), no en `createOp`/`updateOp` (`findRawById` no los trae) -- todos opcionales acá.
export const ClientOperationSchema = z.looseObject({
    id,
    client_id: nullish(id),
    transaction: nullish(z.string()),
    oper_id: nullish(id),
    amount: nullish(z.union([z.string(), z.number()])),
    currency: nullish(z.string()),
    price: nullish(z.union([z.string(), z.number()])),
    operation_time: nullish(z.string()),
    category: nullish(z.string()),
    status: nullish(z.string()),
    notes: nullish(z.string()),
    operation_date: nullish(z.string()),
    created_at: nullish(z.string()),
    updated_at: nullish(z.string()),
    end_date: nullish(z.string()),
    cost: nullish(z.union([z.string(), z.number()])),
    price_amount: nullish(z.union([z.string(), z.number()])),
    business_description: nullish(z.string()),
    short_id: nullish(z.string()),
    process: nullish(z.string()),
    product: nullish(z.string()),
    flow_state: z.unknown().nullish(),
    archived: nullish(z.boolean()),
    industry: nullish(z.string()),
    active_stage_id: nullish(id),
    space_id: nullish(id),
    client_nick: nullish(z.string()),
    client_img: nullish(z.string()),
    client_email: nullish(z.string()),
    oper_nick: nullish(z.string()),
    oper_img: nullish(z.string()),
});

export const ListOpsResponseSchema = z.looseObject({
    ops: z.array(ClientOperationSchema).optional().default([]),
});

export const OpResponseSchema = z.looseObject({
    op: ClientOperationSchema,
});

// `GET /api/client/s/:shortId` (DatabaseScreen.jsx overlay) devuelve el mismo shape que
// `OpResponseSchema` -- confirmado en `GetOpByShortId.js` (mismo `toClientOperationDTO`).
export const GetOpByShortIdResponseSchema = OpResponseSchema;

// ── Flow ({nodes, edges}) ───────────────────────────────────────────────────────────────────
// El shape interno de cada nodo/edge lo define el canvas de SubPanelTratos/OpDetailPanel, no
// el backend (`flow_state` es JSONB libre) -- no hay contrato de campos que validar ahí sin
// acoplar el schema a una forma que puede evolucionar por el lado del frontend. Se valida el
// sobre (`{flow: {nodes: [], edges: []}}`), no el contenido de cada nodo.
export const ClientFlowSchema = z.looseObject({
    nodes: z.array(z.unknown()).optional().default([]),
    edges: z.array(z.unknown()).optional().default([]),
});

export const GetFlowResponseSchema = z.looseObject({
    flow: ClientFlowSchema,
});

export const UpdateFlowResponseSchema = z.looseObject({
    ok: z.boolean(),
});

// ── Documento de cliente (OperationDocumentDTO) ─────────────────────────────────────────────
// `documents[]` lo arma `_attachDocuments`/`resolveDocuments` a partir de `FileAttachment` --
// shape real: `{path, access, ...}` una vez resuelto a URL, campos exactos no contractuales
// fuera de `path` (por eso `looseObject` sin listar cada uno).
export const OperationDocumentSchema = z.looseObject({
    id: nullish(id),
    operation_id: nullish(id),
    client_type: nullish(z.string()),
    document_type: nullish(z.string()),
    form_data: z.unknown().nullish(),
    created_at: nullish(z.string()),
    documents: z.array(z.looseObject({ path: nullish(z.string()) })).optional().default([]),
});

// `GET /api/client_documents` sin `document_type` -> modo "list" (`fetchDocsByUser`).
export const FetchDocsByUserResponseSchema = z.looseObject({
    docs: z.array(OperationDocumentSchema).optional().default([]),
});

// `GET /api/client_documents` con `document_type` -> modo "single" (`fetchDocByType`); `doc`
// puede faltar si `findClientDocuments` no encontró nada (`toOperationDocumentDTO(undefined)`
// devuelve `undefined` tal cual).
export const DocResponseSchema = z.looseObject({
    doc: OperationDocumentSchema.nullish(),
});

// `savePublicDoc`/`saveDoc`/`setDocumentVisibility` devuelven el mismo `{doc: ...}`.
export const SaveDocResponseSchema = DocResponseSchema;

// ── Cosmic / Telefónica ──────────────────────────────────────────────────────────────────────
export const CosmicClientSchema = z.looseObject({
    id,
    line: nullish(z.string()),
    holder: nullish(z.string()),
    plan: nullish(z.string()),
    status: nullish(z.string()),
    balance: nullish(z.union([z.string(), z.number()])),
    consumption_gb: nullish(z.union([z.string(), z.number()])),
    total_gb: nullish(z.union([z.string(), z.number()])),
    message: nullish(z.string()),
    created_at: nullish(z.string()),
    days_remaining: nullish(z.union([z.string(), z.number()])),
    recharge_date: z.union([z.string(), z.null()]).optional(),
});

export const ListCosmicClientsResponseSchema = z.looseObject({
    clients: z.array(CosmicClientSchema).optional().default([]),
    total: nullish(z.number()),
    counts: z.looseObject({
        activas: nullish(z.number()),
        inventario: nullish(z.number()),
        suspendidas: nullish(z.number()),
    }).optional(),
});

export const CosmicClientHistorySchema = z.looseObject({
    id,
    line: nullish(z.string()),
    event_type: nullish(z.string()),
    old_value: nullish(z.string()),
    new_value: nullish(z.string()),
    description: nullish(z.string()),
    created_at: nullish(z.string()),
});

export const ListCosmicHistoryResponseSchema = z.looseObject({
    history: z.array(CosmicClientHistorySchema).optional().default([]),
    total: nullish(z.number()),
});

// `GetAdintelliDashboard.js`: `metrics.balance` siempre viene como string ("0.00" o el valor
// crudo del gateway externo) -- `useAdintelliDashboard` (useCosmicClients.js) ya hace el
// `parseFloat` del lado del frontend, el schema valida la forma cruda, no la parseada.
export const AdintelliDashboardResponseSchema = z.looseObject({
    success: z.boolean(),
    metrics: z.looseObject({
        balance: nullish(z.union([z.string(), z.number()])),
        distributorName: nullish(z.string()),
        activeLines: nullish(z.number()),
        suspendedLines: nullish(z.number()),
        totalLines: nullish(z.number()),
        totalHistoricalConsumption: nullish(z.string()),
    }).optional(),
});

export const FetchLiveBenefitsResponseSchema = z.looseObject({
    updated: z.array(z.looseObject({
        msisdn: z.string(),
        days_remaining: nullish(z.number()),
        recharge_date: z.union([z.string(), z.null()]).optional(),
        consumption_gb: nullish(z.number()),
        total_gb: nullish(z.number()),
        status: nullish(z.string()),
    })).optional().default([]),
});

export const ValidateCosmicLineResponseSchema = z.looseObject({
    valid: z.boolean(),
    message: nullish(z.string()),
    client: CosmicClientSchema.optional(),
});

// ── Comentarios / etapas ────────────────────────────────────────────────────────────────────
export const ClientOperationCommentSchema = z.looseObject({
    id,
    op_id: nullish(id),
    author_id: nullish(id),
    content: nullish(z.string()),
    created_at: nullish(z.string()),
    stage_id: nullish(id),
    author_nick: nullish(z.string()),
    author_img: nullish(z.string()),
});

export const ListCommentsResponseSchema = z.looseObject({
    comments: z.array(ClientOperationCommentSchema).optional().default([]),
});

export const CommentResponseSchema = z.looseObject({
    comment: ClientOperationCommentSchema,
});

export const ClientOperationStageSchema = z.looseObject({
    id,
    op_id: nullish(id),
    title: nullish(z.string()),
    order_index: nullish(z.number()),
    created_at: nullish(z.string()),
});

export const ListStagesResponseSchema = z.looseObject({
    stages: z.array(ClientOperationStageSchema).optional().default([]),
});

export const StageResponseSchema = z.looseObject({
    stage: ClientOperationStageSchema,
});

// ── Usuarios / spaces / permisos / notificaciones ───────────────────────────────────────────
// Shape mínimo real que este feature lee de un usuario -- no el `UserDTO` completo (ese vive en
// `users/schemas.js` si algún día se centraliza; acá solo lo que `ClientsView`/`AddOpModal`
// realmente usan: nick, avatar, rol para el gate de admin).
export const ClientUserSchema = z.looseObject({
    id,
    nick: nullish(z.string()),
    role: z.array(z.string()).optional(),
    'link-profile-img': nullish(z.string()),
    'link-profile-img-thumb': nullish(z.string()),
});

export const ListFlowUsersResponseSchema = z.looseObject({
    users: z.array(ClientUserSchema).optional().default([]),
});

export const CreateUserResponseSchema = z.looseObject({
    success: z.boolean(),
    status: nullish(z.number()),
    user: ClientUserSchema.optional(),
    message: nullish(z.string()),
});

export const UserSpaceSchema = z.looseObject({
    id,
    icon: nullish(z.string()),
    permissions: z.unknown().optional(),
});

export const ListUserSpacesResponseSchema = z.looseObject({
    success: z.boolean(),
    spaces: z.array(UserSpaceSchema).optional().default([]),
});

export const CreateNotificationResponseSchema = z.looseObject({
    success: z.boolean(),
});

export const TabPermissionSchema = z.looseObject({
    tab_id: nullish(z.string()),
    user_id: nullish(id),
});

export const ListTabPermissionsResponseSchema = z.looseObject({
    permissions: z.array(TabPermissionSchema).optional().default([]),
});

export const UpdateTabPermissionsResponseSchema = z.looseObject({
    success: z.boolean(),
});

// ── WhatsApp ─────────────────────────────────────────────────────────────────────────────────
// Agregado junto con `useWhatsAppData.js` -- quedó pendiente en el paso a′ hasta resolver la
// decisión de arquitectura de sockets (02.3-client.md §3/§7 riesgo 6b). Verificado contra
// `backend/src/whatsapp/domain/{WhatsAppChatDTO,WhatsAppMessageDTO,WhatsAppSessionDTO}.js` y los
// casos de uso reales (`GetStatus`/`ListChats`/`ListMessages`/`Connect`/`Logout`/`SendMessage`).
export const WhatsAppChatSchema = z.looseObject({
    id: nullish(id),
    session_name: nullish(z.string()),
    chat_jid: z.string(),
    name: nullish(z.string()),
    avatar_url: nullish(z.string()),
    unread_count: nullish(z.number()),
    last_message_text: nullish(z.string()),
    last_message_timestamp: nullish(z.string()),
    last_message_status: nullish(z.string()),
    updated_at: nullish(z.string()),
});

export const WhatsAppMessageSchema = z.looseObject({
    id: nullish(id),
    chat_jid: z.string(),
    msg_id: nullish(z.string()),
    sender_jid: nullish(z.string()),
    from_me: nullish(z.boolean()),
    text_content: nullish(z.string()),
    status: nullish(z.string()),
    timestamp: nullish(z.string()),
    created_at: nullish(z.string()),
    media_url: nullish(z.string()),
    media_type: nullish(z.string()),
    session_name: nullish(z.string()),
    quoted_msg_id: nullish(z.string()),
    quoted_msg_text: nullish(z.string()),
    quoted_sender_jid: nullish(z.string()),
});

// `GetStatus.execute`: sin sesión devuelve `{success, status:'disconnected', phone:null,
// qr:null}`; con sesión, `{success, ...toWhatsAppSessionDTO(session)}` (agrega
// `custom_name`/`owner_user_id`/`updated_at`). `Connect`/`Logout` devuelven un subconjunto
// (`{success, status}` nada más) -- todos los campos menos `success`/`status` opcionales.
export const WhatsAppStatusResponseSchema = z.looseObject({
    success: z.boolean(),
    status: z.string(),
    phone: nullish(z.string()),
    qr: nullish(z.string()),
    custom_name: nullish(z.string()),
    owner_user_id: nullish(id),
    updated_at: nullish(z.string()),
});

export const WhatsAppChatsResponseSchema = z.looseObject({
    success: z.boolean(),
    chats: z.array(WhatsAppChatSchema).optional().default([]),
});

export const WhatsAppMessagesResponseSchema = z.looseObject({
    success: z.boolean(),
    messages: z.array(WhatsAppMessageSchema).optional().default([]),
});

// `SendMessage.execute` devuelve el `result` crudo de `whatsService.sendMessage` -- `{success:
// true, msgId}` o `{success: false, error}`, nunca el DTO completo del mensaje (por eso el envío
// optimista en el frontend no puede completar `msg_id`/`timestamp` reales desde esta respuesta).
export const SendWhatsAppMessageResponseSchema = z.looseObject({
    success: z.boolean(),
    msgId: nullish(z.string()),
    error: nullish(z.string()),
});

// Envelope real del evento de socket `new-whats-message` (`BaileysWhatsAppService.js`) --
// `{chat_jid, message: {...}}`, no una fila de mensaje suelta. Solo se valida `chat_jid`: con la
// decisión de invalidation-first (ver useWhatsAppData.js), el contenido de `message` ya no se
// consume directamente -- una invalidación vuelve a pedir la lista real, DTO-mapeada, del
// servidor.
export const NewWhatsMessageEnvelopeSchema = z.looseObject({
    chat_jid: z.string(),
    message: z.unknown().optional(),
});

// Payload real del evento `whatsapp-status` (`BaileysWhatsAppService.js`) -- más angosto que
// `WhatsAppStatusResponseSchema` (nunca trae `custom_name`/`owner_user_id`/`updated_at`, esos
// solo salen de `GetStatus`/`toWhatsAppSessionDTO`).
export const WhatsAppStatusSocketSchema = z.looseObject({
    status: z.string(),
    qr: nullish(z.string()),
    phone: nullish(z.string()),
});

// ── WhatsApp -- extensión 02.7-settings.md paso a′ ──────────────────────────────────────────
// `GET /api/whatsapp/stats` -- `whatsAppRouter.js:49-57` devuelve el objeto de
// `PgWhatsAppRepository.getStats` DIRECTO, sin envoltura `{success,...}` (a diferencia de todo lo
// demás en este archivo) -- verificado contra la query real, no contra el estado mock que
// `WhatsAppDashboard.jsx` traía antes de esta migración (02.7-settings.md §7 riesgo 7).
export const WhatsAppStatsResponseSchema = z.looseObject({
    active_conversations: z.number(),
    messages_sent: z.number(),
    messages_received: z.number(),
    rating: z.number(),
});

// `PUT /api/whatsapp/settings` -- también sin `{success,...}` (`whatsAppRouter.js:59-68`).
export const UpdateWhatsAppSettingsResponseSchema = z.looseObject({
    message: z.string(),
});

// `POST /api/whatsapp/chats/:jid/view` -- `markChatReadAndSubscribePresence`'s result.
export const MarkWhatsAppChatViewedResponseSchema = z.looseObject({
    success: z.boolean(),
    error: nullish(z.string()),
});

// `POST /api/whatsapp/chats/:jid/send-media` -- `result` es el retorno crudo de
// `sock.sendMessage()` de Baileys (WAMessage), un shape interno de esa librería que no nos
// corresponde tipar campo por campo -- se valida como opaco a propósito.
export const SendWhatsAppMediaResponseSchema = z.looseObject({
    success: z.boolean(),
    result: z.unknown().optional(),
});

export const UploadWhatsAppShortlinkResponseSchema = z.looseObject({
    success: z.boolean(),
    link: nullish(z.string()),
    shortId: nullish(z.string()),
});

// Payload real de `whatsapp-message-update` (`BaileysWhatsAppService.js:327`).
export const WhatsAppMessageUpdateSocketSchema = z.looseObject({
    chat_jid: z.string(),
    msg_id: z.string(),
    status: z.string(),
});

// Payload real de `whatsapp-presence` (`BaileysWhatsAppService.js:339`) -- `presences` es el
// objeto crudo de Baileys (`update.presences`), shape interno de esa librería, no nuestro para
// tipar -- se valida como opaco a propósito, igual que `result` arriba.
export const WhatsAppPresenceSocketSchema = z.looseObject({
    jid: z.string(),
    presences: z.unknown().optional(),
});

// ── Formularios RHF (paso b′) ──────────────────────────────────────────────────────────────
// `AddOpModal.jsx` -- montos/tiempo se mandan como texto tal cual (mismo comportamiento previo:
// `createOp` los recibe crudos, sin castear a número en el frontend), la única validación real
// hoy es "transacción no vacía". `space_id` no vive aquí -- es un selector con default al space
// activo, no un campo de formulario que el usuario deba llenar.
export const AddOpFormSchema = z.object({
    transaction: z.string().trim().min(1, 'transaction_required'),
    client_id: z.union([z.string(), z.number()]).optional().default(''),
    oper_id: z.union([z.string(), z.number()]).optional().default(''),
    amount: z.string().optional().default(''),
    currency: z.string().default('MXN'),
    price: z.string().optional().default(''),
    cost: z.string().optional().default(''),
    price_amount: z.string().optional().default(''),
    operation_time: z.string().optional().default(''),
    category: z.string().default('todos'),
    status: z.string().default('PLANEADO'),
});
